import type { QRInfo } from "@elektr/shared";
import { formatDate } from "@elektr/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Download, RefreshCw } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, Spinner } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Checkbox, Select } from "@/components/ui/form";
import { useToast } from "@/components/ui/toast";
import { ApiError, api, errorMessage, unwrap } from "@/lib/api";
import { downloadSticker, useQrImage } from "@/lib/qr";
import { useT } from "@/lib/i18n";

import { usePropertyContext } from "./PropertyDetailPage";

export function QrTab() {
  const { property } = usePropertyContext();
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const id = property.id;
  const [lang, setLang] = useState<"uz" | "ru">("uz");
  const [confirm, setConfirm] = useState(false);

  const info = useQuery({
    queryKey: ["qr", id],
    queryFn: async () =>
      unwrap(await api.GET("/api/properties/{id}/qr/", { params: { path: { id } } })),
    retry: false,
  });

  const image = useQrImage(id, info.data?.token);
  const imgUrl = image.data ?? null;

  const sticker = useMutation({
    mutationFn: (fmt: "pdf" | "png") => downloadSticker(id, fmt, lang),
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  const regenerate = useMutation({
    mutationFn: async () =>
      unwrap(await api.POST("/api/properties/{id}/qr/regenerate/", { params: { path: { id } } })),
    onSuccess: (data: QRInfo) => {
      setConfirm(false);
      qc.setQueryData(["qr", id], data);
      toast.success(t("qr.regenerated"));
    },
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  const privacy = useMutation({
    mutationFn: async (value: boolean) =>
      unwrap(
        await api.PATCH("/api/properties/{id}/privacy/", {
          params: { path: { id } },
          body: { show_address_publicly: value },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["qr", id] });
      void qc.invalidateQueries({ queryKey: ["property", id] });
    },
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  if (info.isLoading) return <Spinner className="m-8 size-8" />;
  if (info.isError) {
    const notReady = info.error instanceof ApiError && info.error.status === 409;
    return (
      <Card role="alert" className="border-warning bg-warning/10">
        {notReady ? t("qr.not_ready") : t("common.error")}
      </Card>
    );
  }
  const q = info.data!;

  return (
    <div className="grid gap-4 md:grid-cols-[18rem_minmax(0,1fr)]">
      <Card className="flex flex-col items-center gap-3">
        {imgUrl ? (
          <img src={imgUrl} alt="QR" className="size-60 rounded-lg bg-white p-2" />
        ) : (
          <Spinner className="my-24 size-8" />
        )}
        <a
          className="break-all text-center text-xs text-primary underline"
          href={q.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {q.url}
        </a>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            void navigator.clipboard?.writeText(q.url);
            toast.success(t("qr.copied"));
          }}
        >
          <Copy className="size-4" /> {t("qr.copy")}
        </Button>
      </Card>

      <div className="flex flex-col gap-4">
        <Card>
          <h3 className="mb-3 font-semibold">{t("qr.sticker")}</h3>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              aria-label={t("qr.sticker_lang")}
              className="w-28"
              value={lang}
              onChange={(e) => setLang(e.target.value as "uz" | "ru")}
            >
              <option value="uz">O‘zbekcha</option>
              <option value="ru">Русский</option>
            </Select>
            <Button disabled={sticker.isPending} onClick={() => sticker.mutate("pdf")}>
              <Download className="size-4" /> PDF
            </Button>
            <Button
              variant="outline"
              disabled={sticker.isPending}
              onClick={() => sticker.mutate("png")}
            >
              <Download className="size-4" /> PNG
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{t("qr.sticker_hint")}</p>
        </Card>

        <Card>
          <h3 className="mb-3 font-semibold">{t("qr.stats")}</h3>
          <dl className="grid grid-cols-3 gap-3 text-center">
            <div>
              <dd className="text-2xl font-bold">{q.scans.total}</dd>
              <dt className="text-xs text-muted-foreground">{t("qr.scans_total")}</dt>
            </div>
            <div>
              <dd className="text-2xl font-bold">{q.scans.last_30_days}</dd>
              <dt className="text-xs text-muted-foreground">{t("qr.scans_30")}</dt>
            </div>
            <div>
              <dd className="text-sm font-semibold">
                {q.scans.last_scan_at ? formatDate(q.scans.last_scan_at) : "—"}
              </dd>
              <dt className="text-xs text-muted-foreground">{t("qr.scans_last")}</dt>
            </div>
          </dl>
        </Card>

        <Card>
          <Checkbox
            label={t("props.show_address")}
            checked={q.show_address_publicly}
            disabled={privacy.isPending}
            onChange={(e) => privacy.mutate(e.target.checked)}
          />
          <p className="text-xs text-muted-foreground">{t("props.show_address_hint")}</p>
        </Card>

        <Card className="border-danger/40">
          <h3 className="mb-1 font-semibold">{t("qr.regenerate")}</h3>
          <p className="mb-3 text-sm text-muted-foreground">
            {t("qr.regenerate_hint", { n: q.history_count })}
          </p>
          <Button
            variant="danger"
            className="whitespace-normal py-2"
            onClick={() => setConfirm(true)}
          >
            <RefreshCw className="size-4" /> {t("qr.regenerate")}
          </Button>
        </Card>
      </div>

      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title={t("qr.regenerate")}
        message={t("qr.regenerate_confirm")}
        confirmLabel={t("qr.regenerate")}
        pending={regenerate.isPending}
        onConfirm={() => regenerate.mutate()}
      />
    </div>
  );
}
